import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore, useTopicsStore, useCardsStore } from '@/stores'

interface CognitoTokenResponse {
  id_token: string
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

interface JWTPayload {
  sub: string
  email: string
  name?: string
  'cognito:username'?: string
  exp: number
  iat: number
}

function parseJwt(token: string): JWTPayload {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(jsonPayload)
}

export default function CallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setUser, setError, setLoading } = useAuthStore()
  const { fetchTopics } = useTopicsStore()
  const { fetchCards } = useCardsStore()

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code')
      const error = searchParams.get('error')

      if (error) {
        setError('Authentication failed')
        navigate('/login')
        return
      }

      if (!code) {
        setError('No authorization code received')
        navigate('/login')
        return
      }

      setLoading(true)

      try {
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
        const redirectUri = window.location.origin + '/callback'

        // Exchange code for tokens
        const tokenResponse = await fetch(`${cognitoDomain}/oauth2/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            code,
            redirect_uri: redirectUri,
          }),
        })

        if (!tokenResponse.ok) {
          throw new Error('Token exchange failed')
        }

        const tokens: CognitoTokenResponse = await tokenResponse.json()

        // Parse ID token to get user info
        const payload = parseJwt(tokens.id_token)

        const user = {
          id: payload.sub,
          email: payload.email,
          name: payload.name || payload.email.split('@')[0],
          createdAt: new Date().toISOString(),
        }

        // Store tokens
        localStorage.setItem('branka_id_token', tokens.id_token)
        localStorage.setItem('branka_access_token', tokens.access_token)
        localStorage.setItem('branka_refresh_token', tokens.refresh_token)
        localStorage.setItem('branka_user', JSON.stringify(user))

        setUser(user)

        // Fetch initial data after successful login
        try {
          await Promise.all([fetchTopics(), fetchCards()])
        } catch (fetchError) {
          console.error('Failed to fetch initial data:', fetchError)
        }

        navigate('/dashboard')
      } catch (err) {
        console.error('Auth error:', err)
        setError('Failed to complete authentication')
        navigate('/login')
      }
    }

    handleCallback()
  }, [searchParams, navigate, setUser, setError, setLoading, fetchTopics, fetchCards])

  return (
    <div className="loading-container">
      <div className="loading-spinner" />
      <p>Completing sign in...</p>
    </div>
  )
}
